import type { NotificationData } from '../../services/notification.service';

export const GET_LP_IMAGE_PREPAID_REDEEM_FLOW_STATUS = 'redeem-prepaid-purchase';

/** Inner payload from `POST order/get-lp-image` when flow status is redeem-prepaid-purchase. */
export function getGetLpImagePrepaidRedeemInnerPayload(
  apiData: Record<string, any> | null | undefined
): Record<string, any> | null {
  if (!apiData || typeof apiData !== 'object') {
    return null;
  }
  const nested = apiData['data'];
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    return nested as Record<string, any>;
  }
  return apiData;
}

/** Detects `response.data.status === 'redeem-prepaid-purchase'` on get-lp-image API body. */
export function isGetLpImagePrepaidRedeemResponse(
  apiData: Record<string, any> | null | undefined
): boolean {
  if (!apiData || typeof apiData !== 'object') {
    return false;
  }
  return apiData['status'] === GET_LP_IMAGE_PREPAID_REDEEM_FLOW_STATUS;
}

function str(value: any): string {
  return value != null ? String(value).trim() : '';
}

/** Adds bootstrap ids/flags for notification-modal prepaid redeem flow. */
export function enrichGetLpImagePayloadForPrepaidRedeemBootstrap(
  payload: Record<string, any>
): Record<string, any> {
  const prepaidPurchaseId = str(payload['prepaid_purchase_id'] || payload['prepaidPurchaseId']);
  const customerId = str(payload['customer_id'] || payload['customerId']);
  const vehicleId = str(payload['vehicle_id'] || payload['vehicleId']);

  return {
    ...payload,
    prepaid_purchase_id: prepaidPurchaseId || payload['prepaid_purchase_id'],
    customer_id: customerId || payload['customer_id'],
    customerId: customerId || payload['customerId'],
    vehicle_id: vehicleId || payload['vehicle_id'],
    vehicleId: vehicleId || payload['vehicleId'],
    lpr_prepaid_redeem: true,
  };
}

/** Builds create-order-request notification fields from get-lp-image prepaid redeem response. */
export function buildCreateOrderRequestFromGetLpImagePrepaidRedeem(
  apiData: Record<string, any>
): Pick<NotificationData, 'type' | 'message' | 'data'> {
  const inner = getGetLpImagePrepaidRedeemInnerPayload(apiData) ?? {};
  const data = enrichGetLpImagePayloadForPrepaidRedeemBootstrap(inner);
  const message = str(inner['message']) || 'Active prepaid wash book found. Prompt redemption.';

  return {
    type: 'create-order-request',
    message,
    data,
  };
}

const PREPAID_REDEEM_DEFAULT_MESSAGE = 'Active prepaid wash book found. Prompt redemption.';

/** Parse Pusher callback payload (object or stringified JSON). */
export function parsePusherEventPayload(raw: any): Record<string, any> {
  if (typeof raw === 'string') {
    try {
      const parsed: any = JSON.parse(raw);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed as Record<string, any>;
      }
    } catch {
      return {};
    }
    return {};
  }
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    return raw as Record<string, any>;
  }
  return {};
}

/**
 * Maps lane LPR Pusher `redeem-prepaid-purchase` to create-order-request for the modal.
 * Same bootstrap as get-lp-image prepaid redeem; does not set `is_lpr_upload`.
 */
export function buildCreateOrderRequestFromLanePrepaidRedeemPusher(
  raw: any
): Pick<NotificationData, 'type' | 'message' | 'data'> {
  const flat = parsePusherEventPayload(raw);
  const enriched = enrichGetLpImagePayloadForPrepaidRedeemBootstrap({
    ...flat,
    event: GET_LP_IMAGE_PREPAID_REDEEM_FLOW_STATUS,
    source_context: 'lpr',
  });
  const message = str(enriched['message']) || PREPAID_REDEEM_DEFAULT_MESSAGE;

  return {
    type: 'create-order-request',
    message,
    data: enriched,
  };
}

/** LPR entry `event` or row flag for prepaid wash book redemption (lane / history list). */
export function isPrepaidRedeemLprNotification(
  eventOrType: any,
  data?: Record<string, any> | null
): boolean {
  const key = str(eventOrType);
  if (key === GET_LP_IMAGE_PREPAID_REDEEM_FLOW_STATUS) {
    return true;
  }
  if (!data || typeof data !== 'object') {
    return false;
  }
  if (data['lpr_prepaid_redeem'] === true) {
    return true;
  }
  return str(data['event']) === GET_LP_IMAGE_PREPAID_REDEEM_FLOW_STATUS;
}

/** Flatten nested LPR row payload (`data.data`) for prepaid bootstrap fields. */
export function flattenLprEntryRowPayload(row: Record<string, any>): Record<string, any> {
  const nested = row['data'];
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    return { ...row, ...(nested as Record<string, any>) };
  }
  return { ...row };
}

/**
 * Maps stored `redeem-prepaid-purchase` LPR rows to create-order-request so the modal
 * loads prepaid purchase status and the redeem UI (same as live get-lp-image flow).
 */
export function normalizePrepaidRedeemNotificationForModal(
  notification: NotificationData
): NotificationData {
  const payload = (notification.data ?? {}) as Record<string, any>;
  const flat = flattenLprEntryRowPayload(payload);
  if (
    !isPrepaidRedeemLprNotification(notification.type, flat) &&
    !isPrepaidRedeemLprNotification(flat['event'], flat)
  ) {
    return notification;
  }

  const enriched = enrichGetLpImagePayloadForPrepaidRedeemBootstrap(flat);
  const message =
    str(notification.message) || str(enriched['message']) || PREPAID_REDEEM_DEFAULT_MESSAGE;

  return {
    ...notification,
    type: 'create-order-request',
    message,
    data: enriched,
  };
}
